/**
 * Safety incident entity + status machine (REQ-SAF-06, REQ-SAF-08, REQ-SAF-09, REQ-BM-017).
 *
 * A safety report or an SOS creates an incident linked to the ride and the reporter; the incident advances
 * through a legal-edge status machine and every transition appends an immutable audit entry. Safety is a
 * default standard, not an add-on (REQ-BM-017).
 */
import { err, ok } from '@vello/core';
export const SAFETY_INCIDENT_STATUSES = [
    'OPEN',
    'TRIAGED',
    'IN_REVIEW',
    'ESCALATED',
    'RESOLVED',
    'CLOSED',
];
const EDGES = {
    OPEN: ['TRIAGED', 'ESCALATED', 'CLOSED'],
    TRIAGED: ['IN_REVIEW', 'ESCALATED', 'RESOLVED', 'CLOSED'],
    IN_REVIEW: ['ESCALATED', 'RESOLVED', 'CLOSED'],
    ESCALATED: ['IN_REVIEW', 'RESOLVED', 'CLOSED'],
    RESOLVED: ['CLOSED'],
    CLOSED: [],
};
export function canIncidentTransition(from, to) {
    return EDGES[from].includes(to);
}
export const SAFETY_INCIDENT_TYPES = [
    'SOS',
    'HARASSMENT',
    'ACCIDENT',
    'UNSAFE_DRIVING',
    'ROUTE_CONCERN',
    'LOST_ITEM',
    'OTHER',
];
export const SAFETY_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
/**
 * The operator queue an incident is routed to besides the safety queue every incident is in (W7-DRIVER-TRIP-TRUTH, D416;
 * owner decision D408 #6): a DRIVER's SOS or trip report goes to the OPS queue — the operations team of the ride's market,
 * which holds `ops:view` but not `safety:view`. Absent = the safety queue only (every incident before D416, and a rider's).
 */
export const INCIDENT_QUEUES = ['OPS'];
/**
 * Where the reporter was when the incident was raised (W8-RIDER-SAFETY-CONSENT, D440; finding R-06). `source` says who
 * produced the fix: `DEVICE` — the reporter's own device sent it; `RIDE_DRIVER_FIX` — the server attached the assigned
 * driver's last fresh fix from the hot location model because the reporter is on that ride (the rider app has no
 * device GPS until the native track). Degrees WGS84, accuracy in meters, instants UTC.
 */
export const INCIDENT_LOCATION_SOURCES = ['DEVICE', 'RIDE_DRIVER_FIX'];
/** A plausible WGS84 fix (finite, in range; accuracy non-negative when present). */
export function validIncidentLocation(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return false;
    const o = v;
    const lat = o.latitude;
    const lng = o.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng))
        return false;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180)
        return false;
    if (o.accuracyMeters !== undefined && (typeof o.accuracyMeters !== 'number' || !Number.isFinite(o.accuracyMeters) || o.accuracyMeters < 0))
        return false;
    return o.capturedAt === undefined || (typeof o.capturedAt === 'string' && Number.isFinite(Date.parse(o.capturedAt)));
}
export function createIncident(input, now) {
    return {
        incidentId: input.incidentId,
        type: input.type,
        severity: input.severity,
        status: 'OPEN',
        reporterId: input.reporterId,
        rideId: input.rideId,
        subjectId: input.subjectId,
        evidenceRefs: input.evidenceRefs ?? [],
        reasonCode: input.reasonCode ?? 'REPORTED',
        ...(input.queue !== undefined ? { queue: input.queue } : {}),
        ...(input.reporterRole !== undefined ? { reporterRole: input.reporterRole } : {}),
        ...(input.location !== undefined ? { location: input.location } : {}),
        createdAt: now,
        updatedAt: now,
        audit: [
            {
                at: now,
                actorId: input.reporterId,
                action: 'CREATED',
                reasonCode: input.reasonCode ?? 'REPORTED',
                toStatus: 'OPEN',
            },
        ],
    };
}
/** A safety report submission (REQ-SAF-06) — a rider/driver-initiated incident. */
export function submitSafetyReport(input, now) {
    return createIncident(input, now);
}
export function raiseEmergency(input, config, now) {
    const incident = createIncident({ ...input, type: 'SOS', severity: 'CRITICAL', reasonCode: input.reasonCode ?? 'SOS' }, now);
    return {
        ...incident,
        emergency: {
            emergencyContactRef: config.emergencyContactRef,
            notifyTrustedContacts: config.notifyTrustedContacts,
        },
        audit: [
            ...incident.audit,
            { at: now, actorId: input.reporterId, action: 'EMERGENCY_ROUTED', reasonCode: config.emergencyContactRef },
        ],
    };
}
export function transitionIncident(incident, to, actorId, reasonCode, now) {
    if (!canIncidentTransition(incident.status, to)) {
        return err({ code: 'ILLEGAL_TRANSITION', message: `${incident.status} -> ${to}` });
    }
    return ok({
        ...incident,
        status: to,
        updatedAt: now,
        audit: [
            ...incident.audit,
            { at: now, actorId, action: 'TRANSITION', reasonCode, fromStatus: incident.status, toStatus: to },
        ],
    });
}
/** Attach an evidence reference (e.g. an encrypted audio ref) to an incident with an audit trail. */
export function attachEvidence(incident, evidenceRef, actorId, now) {
    return {
        ...incident,
        evidenceRefs: [...incident.evidenceRefs, evidenceRef],
        updatedAt: now,
        audit: [...incident.audit, { at: now, actorId, action: 'ATTACH_EVIDENCE', reasonCode: 'EVIDENCE' }],
    };
}
//# sourceMappingURL=incident.js.map